package com.mxpainting.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.mxpainting.Language
import com.mxpainting.Strings
import com.mxpainting.rememberLanguage
import com.mxpainting.components.layouts.PageLayout
import com.varabyte.kobweb.compose.css.CSSColor
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.disabled
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.jetbrains.compose.web.dom.TextInput
import kotlinx.serialization.json.Json

@Page
@Composable
fun ContactPage() {
    val language by rememberLanguage()
    
    PageLayout {
        Column(Modifier.fillMaxSize()) {
            // Header
            Div(Modifier.classNames("container-xxl", "py-5", "bg-light", "mb-5").toAttrs()) {
                Div(Modifier.classNames("container", "text-center").toAttrs()) {
                    H1(Modifier.classNames("display-3", "mb-4").toAttrs()) { 
                        Text(Strings.contact[language].orEmpty()) 
                    }
                    P(Modifier.classNames("fs-5", "text-body").toAttrs()) {
                        Text(Strings.contactText[language].orEmpty())
                    }
                }
            }

            // Contact info cards
            Div(Modifier.classNames("container-xxl", "py-5").toAttrs()) {
                Div(Modifier.classNames("container").toAttrs()) {
                    Div(Modifier.classNames("row", "g-4").toAttrs()) {
                        ContactInfoItem(
                            "fa-map-marker-alt", 
                            Strings.contactOfficeCardHeader[language].orEmpty(),
                            Strings.contactOfficeCardDescription[language].orEmpty()
                        )
                        ContactInfoItem(
                            "fa-phone-alt", 
                            Strings.contactPhoneCardHeader[language].orEmpty(),
                            Strings.contactPhoneCardDescription[language].orEmpty(), link = "tel:+2508849487"
                        )
                        ContactInfoItem(
                            "fa-envelope", 
                            Strings.contactEmailCardHeader[language].orEmpty(),
                            Strings.contactEmailCardDescription[language].orEmpty(), link = "mailto:mxpainting@gmail.com"
                        )
                    }
                }
            }

            // Form & Map
            Div(Modifier.classNames("container-xxl", "py-5").toAttrs()) {
                Div(Modifier.classNames("container").toAttrs()) {
                    Div(Modifier.classNames("row", "g-5").toAttrs()) {
                        // Contact Form
                        ContactForm(language = language)

                        // Google Map
                        GoogleMap(language = language)
                    }
                }
            }
        }
    }
}

@Composable
private fun ContactInfoItem(icon: String, title: String, description: String, link: String? = null) {
    Div(Modifier.classNames("col-md-4").toAttrs()) {
        Div(Modifier.classNames("contact-info-item").toAttrs()) {
            link?.let {
                A(href = it, Modifier.classNames("mb-0").color(CSSColor.Inherit).textDecorationLine(TextDecorationLine.None).toAttrs()) {
                    Div(Modifier.classNames("contact-info-icon").toAttrs()) {
                I(Modifier.classNames("fa", icon).toAttrs()) {}
            }
                    H4(Modifier.classNames("mb-3").toAttrs()) { Text(title) }
                    Text(description)
                }
            } ?: run {
                Div(Modifier.classNames("contact-info-icon").toAttrs()) {
                    I(Modifier.classNames("fa", icon).toAttrs()) {}
                }
                H4(Modifier.classNames("mb-3").toAttrs()) { Text(title) }
                P(Modifier.classNames("mb-0").toAttrs()) { Text(description) }
            }
        }
    }
}

@Composable
private fun GoogleMap(language: Language) {
    Div(Modifier.classNames("col-lg-6").toAttrs()) {
        H2(Modifier.classNames("display-6", "mb-4").toAttrs()) {
            Text(Strings.contactOurLocation[language].orEmpty())
        }
        Iframe(attrs = Modifier.classNames("w-100", "rounded").styleModifier {
            height(450.px)
            property("border", "0")
        }.toAttrs {
            attr("src", "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d171120.58989502758!2d-123.47648316694726!3d48.42842065839083!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548f738bddb06171%3A0x38e8f3741ebb48ed!2sVictoria%2C%20BC!5e0!3m2!1sen!2sca!4v1680000000000!5m2!1sen!2sca")
            attr("allowfullscreen", "")
            attr("loading", "lazy")
        })
    }
}

@Composable
private fun ContactForm(language: Language) {

    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }

    var turnstileToken by remember { mutableStateOf<String?>(null) }
    var isSubmitting by remember { mutableStateOf(false) }
    var submitResult by remember { mutableStateOf<Pair<Boolean, String>?>(null) }

    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        kotlinx.browser.window.asDynamic().onSuccess = { token: String ->
            turnstileToken = token
        }
        kotlinx.browser.window.asDynamic().onTurnstileError = { 
            submitResult = false to (Strings.contactFormError[language].orEmpty())
        }
    }

    Div(Modifier.classNames("col-lg-6").toAttrs()) {
        H2(Modifier.classNames("display-6", "mb-4").toAttrs()) {
            Text(Strings.contactSendUsAMessage[language].orEmpty())
        }
        Form(attrs = Modifier.toAttrs {
            onSubmit {
                it.preventDefault()
                if (isSubmitting) return@onSubmit

                isSubmitting = true
                submitResult = null

                val request = com.mxpainting.models.ContactRequest(name, email, phone, message, turnstileToken)
                val json = Json.encodeToString(com.mxpainting.models.ContactRequest.serializer(), request)

                kotlinx.browser.window.fetch("/api/contact", org.w3c.fetch.RequestInit(
                    method = "POST",
                    body = json,
                    headers = org.w3c.fetch.Headers("Content-Type" to "application/json")
                )).then { response ->
                    isSubmitting = false
                    if (response.ok) {
                        submitResult = true to (Strings.contactMessageSent[language].orEmpty())
                        name = "" ; email = "" ; phone = ""; message = ""
                    } else {
                        submitResult = false to (Strings.contactMessageError[language].orEmpty())
                    }
                }.catch {
                    isSubmitting = false
                    submitResult = false to (Strings.contactMessageError[language].orEmpty())
                }
            }
        }) {

            Div(Modifier.classNames("row", "g-3").toAttrs()) {
                Div(Modifier.classNames("col-md-12").toAttrs()) {
                    Div(Modifier.classNames("form-floating").toAttrs()) {
                        TextInput(name, attrs = Modifier.classNames("form-control").id("name").toAttrs {
                            placeholder(Strings.contactName[language].orEmpty())
                            onInput { name = it.target.value }
                            required()
                        })
                        Label(forId = "name") { Text(Strings.contactName[language].orEmpty()) }
                    }
                }
                Div(Modifier.classNames("col-md-6").toAttrs()) {
                    Div(Modifier.classNames("form-floating").toAttrs()) {
                        TextInput(email, attrs = Modifier.classNames("form-control").id("email").toAttrs {
                            placeholder(Strings.contactEmail[language].orEmpty())
                            onInput { email = it.target.value }
                            required()
                        })
                        Label(forId = "email") { Text(Strings.contactEmail[language].orEmpty()) }
                    }
                }
                Div(Modifier.classNames("col-6").toAttrs()) {
                    Div(Modifier.classNames("form-floating").toAttrs()) {
                        TextInput(phone, attrs = Modifier.classNames("form-control").id("phone").toAttrs {
                            placeholder(Strings.contactPhone[language].orEmpty())
                            onInput { phone = it.target.value }
                            required()
                        })
                        Label(forId = "phone") { Text(Strings.contactPhone[language].orEmpty()) }
                    }
                }
                Div(Modifier.classNames("col-12").toAttrs()) {
                    Div(Modifier.classNames("form-floating").toAttrs()) {
                        TextArea(message, attrs = Modifier.classNames("form-control").id("message").styleModifier { height(150.px) }.toAttrs {
                            placeholder("Message")
                            onInput { message = it.target.value }
                            required()
                        })
                        Label(forId = "message") { Text(Strings.contactMessage[language].orEmpty()) }
                    }
                }

                submitResult?.let { (success, message) ->
                    Div(Modifier.classNames("col-12").toAttrs()) {
                        Div(Modifier.classNames("alert", if (success) "alert-success" else "alert-danger").toAttrs()) {
                            Text(message)
                        }
                    }
                }

                Div(Modifier.classNames("col-12").toAttrs()) {
                    Button(attrs = Modifier.classNames("btn", "btn-primary", "w-100", "py-3").toAttrs {
                        if (isSubmitting || turnstileToken == null) disabled()
                    }) {
                        if (isSubmitting) {
                            Span(Modifier.classNames("spinner-border", "spinner-border-sm", "me-2").toAttrs()) {}
                            Text(Strings.contactSending[language].orEmpty())
                        } else {
                            Text(Strings.contactSend[language].orEmpty())
                        }
                    }
                }
                // Cloudflare turnstile Item
                Div(Modifier.classNames("cf-turnstile").toAttrs{ 
                    attr("data-sitekey", "0x4AAAAAAC5vqoS24Nk0srnJ")
                    attr("data-theme", "dark")
                    attr("data-size", "small")
                    attr("data-callback", "onSuccess")
                    attr("data-error-callback", "onTurnstileError")
                }) {}
            }
        }
    }
}